/**
 * Zápisové operace. Každá proběhne v jedné transakci, takže například
 * přesun včelstva změní stanoviště i zapíše událost zároveň.
 */
import type {
  Apiary,
  BaseEntity,
  Colony,
  ColonyEvent,
  ISODate,
  Product,
  Queen,
  QueenChangeEvent,
  SplitEvent,
} from './model';
import { applySuperChange, dateProblems, dropUndefined, isLatestOfKind, newEntity, nowISO, splitPerColony, validateEvent } from './rules';
import { tx, type StoreName, type StoreMap, type Tx } from './db';

type New<T extends BaseEntity> = Omit<T, keyof BaseEntity>;
type Editable<T extends BaseEntity> = New<T> & Partial<Pick<T, 'id'>>;

/** Vytvoří nebo aktualizuje záznam (podle přítomnosti `id`). */
async function upsert<S extends Exclude<StoreName, 'settings'>>(
  store: S,
  data: Editable<StoreMap[S]>,
): Promise<StoreMap[S]> {
  return tx([store], 'readwrite', async (t) => {
    let row: StoreMap[S];
    if (data.id) {
      const existing = await t.get(store, data.id);
      if (!existing) throw new Error('Záznam nenalezen.');
      row = { ...existing, ...data, updatedAt: nowISO() } as StoreMap[S];
    } else {
      row = newEntity(data as never) as StoreMap[S];
    }
    await t.put(store, row);
    return row;
  });
}

export const saveApiary = (a: Editable<Apiary>) => upsert('apiaries', a);
export const saveColony = (c: Editable<Colony>) => upsert('colonies', c);
export const saveQueen = (q: Editable<Queen>) => upsert('queens', q);
export const saveProduct = (p: Editable<Product>) => upsert('products', p);

/** Měkké smazání (do koše) nebo obnovení. */
export function setDeleted(store: Exclude<StoreName, 'settings'>, id: string, deleted: boolean) {
  return tx([store], 'readwrite', async (t) => {
    const row = await t.get(store, id);
    if (!row) return;
    await t.put(store, { ...row, deletedAt: deleted ? nowISO() : null, updatedAt: nowISO() } as never);
  });
}

export class ValidationError extends Error {
  constructor(public errors: string[]) {
    super(errors.join(' '));
  }
}

/**
 * Uloží událost a promítne její důsledky do včelstev:
 * přesun → stanoviště, prohlídka → počet nástavků, změna stavu → status.
 *
 * Nový zápis pro více včelstev se rozdělí na samostatné záznamy
 * (každé včelstvo má svůj, spojuje je batchId). Pole s hodnotou
 * undefined se při úpravě ze záznamu odstraní.
 */
export async function saveEvent(
  data: Editable<ColonyEvent>,
): Promise<ColonyEvent> {
  const ts = nowISO();
  return tx(['events', 'colonies'], 'readwrite', async (t) => {
    let event: ColonyEvent;
    if (data.id) {
      const existing = await t.get('events', data.id);
      if (!existing) throw new Error('Záznam nenalezen.');
      event = dropUndefined({ ...existing, ...data, updatedAt: ts } as ColonyEvent);
    } else {
      event = newEntity(dropUndefined({ ...data }) as never) as ColonyEvent;
    }
    const errors = validateEvent(event);
    if (errors.length) throw new ValidationError(errors);
    const parts = data.id ? [event] : splitPerColony(event);
    for (const part of parts) await applyEvent(t, part, ts, !data.id);
    return parts[0];
  });
}

async function applyEvent(t: Tx, event: ColonyEvent, ts: string, isNew: boolean) {
  for (const colonyId of event.colonyIds) {
    const colony = await t.get('colonies', colonyId);
    // nový zápis k neexistujícímu včelstvu (např. starý odkaz nebo štítek) by v datech jen překážel
    if (!colony && isNew) throw new ValidationError(['Včelstvo nebylo nalezeno – zápis nejde uložit.']);
    if (!colony) continue;
    // aktuální stav včelstva mění jen nejnovější zápis daného druhu
    const history = await t.byIndex('events', 'colonyIds', colonyId);
    const latest = (same: (e: ColonyEvent) => boolean) => isLatestOfKind(event, history, same);
    let changed: Colony | null = null;
    if (event.type === 'move' && latest((e) => e.type === 'move')) {
      changed = { ...colony, apiaryId: event.toApiaryId };
    } else if (
      event.type === 'inspection' &&
      event.boxCountAfter != null &&
      latest((e) => e.type === 'inspection' && e.boxCountAfter != null)
    ) {
      changed = { ...colony, boxCount: event.boxCountAfter };
    } else if (event.type === 'super_change' && isNew) {
      // jen u nového zápisu – úprava starého zápisu medníky znovu nepřidává
      changed = { ...colony, supers: applySuperChange(colony.supers, event) };
    } else if (event.type === 'status_change' && latest((e) => e.type === 'status_change')) {
      changed = { ...colony, status: event.newStatus, statusDate: event.date };
    }
    if (changed) await t.put('colonies', { ...changed, updatedAt: ts });
  }
  await t.put('events', event);
}

const notFuture = (date: string) => {
  const errors = dateProblems(date);
  if (errors.length) throw new ValidationError(errors);
};

/** Výměna matky: nová matka + událost + aktualizace včelstva a staré matky. */
export async function replaceQueen(args: {
  colonyId: string;
  apiaryId: string;
  date: ISODate;
  newQueen: New<Queen> | null;
  reason?: QueenChangeEvent['reason'];
  note?: string;
}): Promise<void> {
  notFuture(args.date);
  const ts = nowISO();
  await tx(['colonies', 'queens', 'events'], 'readwrite', async (t) => {
    const colony = await t.get('colonies', args.colonyId);
    if (!colony) throw new Error('Včelstvo nenalezeno.');
    const old = colony.currentQueenId ? await t.get('queens', colony.currentQueenId) : undefined;
    if (old) {
      await t.put('queens', {
        ...old,
        status: args.reason === 'lost' ? 'lost' : 'replaced',
        removedAt: args.date,
        updatedAt: ts,
      });
    }
    let newQueenId: string | null = null;
    if (args.newQueen) {
      const q = newEntity<Queen>({
        ...args.newQueen,
        colonyId: colony.id,
        introducedAt: args.date,
        status: 'present',
      });
      await t.put('queens', q);
      newQueenId = q.id;
    }
    await t.put('colonies', { ...colony, currentQueenId: newQueenId, updatedAt: ts });
    await t.put(
      'events',
      newEntity<QueenChangeEvent>({
        type: 'queen_change',
        date: args.date,
        colonyIds: [colony.id],
        apiaryId: args.apiaryId,
        oldQueenId: old?.id ?? null,
        newQueenId,
        reason: args.reason,
        note: args.note,
      }),
    );
  });
}

/** Oddělek: nové včelstvo s odkazem na mateřské + událost u mateřského. */
export async function createSplit(args: {
  parentId: string;
  apiaryId: string;
  label: string;
  date: ISODate;
  framesTaken?: number;
  note?: string;
}): Promise<Colony> {
  notFuture(args.date);
  return tx(['colonies', 'events'], 'readwrite', async (t) => {
    const parent = await t.get('colonies', args.parentId);
    if (!parent) throw new Error('Mateřské včelstvo nenalezeno.');
    const colony = newEntity<Colony>({
      apiaryId: args.apiaryId,
      label: args.label,
      hiveSystem: parent.hiveSystem,
      frameSize: parent.frameSize,
      boxCount: 1,
      status: 'active',
      origin: 'split',
      originDate: args.date,
      parentColonyId: parent.id,
      currentQueenId: null,
    });
    await t.put('colonies', colony);
    await t.put(
      'events',
      newEntity<SplitEvent>({
        type: 'split',
        date: args.date,
        colonyIds: [parent.id],
        apiaryId: parent.apiaryId,
        newColonyId: colony.id,
        framesTaken: args.framesTaken,
        note: args.note,
      }),
    );
    return colony;
  });
}
